(() => {
  'use strict';
  const strings = JSON.parse(document.getElementById('ReeshanStrings')?.textContent || '{}');
  const root = window.Shopify?.routes?.root || document.body.dataset.root || '/';
  const route = path => new URL(root + path, window.location.origin);
  const parse = text => new DOMParser().parseFromString(text, 'text/html');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wishlistKey = 'reeshan:wishlist:v1';
  const recentKey = 'reeshan:recent:v1';
  const returnFocus = new WeakMap();
  const galleryBindings = new WeakSet();
  const productRequests = new WeakMap();
  const cardCache = new Map();
  let toastTimer;
  let cartQueue = Promise.resolve();
  let cartPending = 0;
  let wishlistRevision = 0;
  let searchRequest;
  let searchTimer;
  let originalSuggestions;

  function notify(message) {
    const status = document.getElementById('ReeshanStatus');
    if (!status) return;
    clearTimeout(toastTimer);
    status.textContent = message || strings.error;
    status.hidden = false;
    toastTimer = setTimeout(() => { status.hidden = true; }, 4500);
  }

  function closeDialog(dialog, restore = true) {
    if (!dialog?.open) return;
    dialog.close();
    document.querySelectorAll('[data-dialog-open]').forEach(trigger => {
      if (trigger.dataset.dialogOpen === dialog.id) trigger.setAttribute('aria-expanded', 'false');
    });
    document.documentElement.classList.toggle('modal-open', !!document.querySelector('dialog[open]'));
    const previous = returnFocus.get(dialog);
    if (restore && previous?.isConnected) previous.focus({ preventScroll: true });
  }

  function openDialog(id, trigger) {
    const dialog = document.getElementById(id);
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    document.querySelectorAll('dialog[open]').forEach(open => { if (open !== dialog) closeDialog(open, false); });
    returnFocus.set(dialog, trigger || document.activeElement);
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('modal-open');
    trigger?.setAttribute('aria-expanded', 'true');
    if (id === 'SearchDialog') dialog.querySelector('input[type=search]')?.focus();
    return true;
  }

  async function jsonRequest(url, options = {}) {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    let data;
    try { data = await response.json(); } catch { throw new Error(strings.error); }
    if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : strings.error);
    return data;
  }

  function readHandles(key) {
    try {
      const list = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(list) ? [...new Set(list.filter(value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(value)))].slice(0, 100) : [];
    } catch { return []; }
  }

  function writeHandles(key, handles) {
    try { localStorage.setItem(key, JSON.stringify(handles)); return true; }
    catch { notify(strings.storage); return false; }
  }

  function syncWishlist() {
    const saved = readHandles(wishlistKey);
    document.querySelectorAll('[data-wishlist-toggle]').forEach(button => {
      button.setAttribute('aria-pressed', String(saved.includes(button.dataset.wishlistToggle)));
    });
    document.querySelectorAll('[data-wishlist-count]').forEach(counter => {
      counter.textContent = String(saved.length);
      if (counter.classList.contains('utility-count')) counter.hidden = saved.length === 0;
    });
  }

  async function cardFor(handle) {
    if (!/^[a-z0-9][a-z0-9-]*$/.test(handle)) return null;
    if (!cardCache.has(handle)) {
      const url = route('products/' + encodeURIComponent(handle));
      url.searchParams.set('section_id', 'product-card-fragment');
      const task = fetch(url, { credentials: 'same-origin' }).then(async response => {
        if (response.status === 404) return null;
        if (!response.ok) throw new Error(strings.error);
        return parse(await response.text()).querySelector('.product-card');
      }).catch(error => { cardCache.delete(handle); throw error; });
      cardCache.set(handle, task);
    }
    const card = await cardCache.get(handle);
    return card?.cloneNode(true) || null;
  }

  function skeletons(count) {
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const placeholder = document.createElement('div');
      placeholder.className = 'product-skeleton';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.innerHTML = '<div class="skeleton-image"></div><div class="skeleton-line"></div><div class="skeleton-line short"></div>';
      fragment.append(placeholder);
    }
    return fragment;
  }

  async function renderWishlist() {
    const page = document.querySelector('[data-wishlist-page]');
    if (!page) return;
    const revision = ++wishlistRevision;
    const handles = readHandles(wishlistKey);
    const grid = page.querySelector('[data-wishlist-grid]');
    const empty = page.querySelector('[data-wishlist-empty]');
    const error = page.querySelector('[data-wishlist-error]');
    grid.replaceChildren(skeletons(Math.min(handles.length, 4)));
    grid.setAttribute('aria-busy', String(handles.length > 0));
    empty.hidden = handles.length > 0;
    error.hidden = true;
    const cards = [];
    let failed = false;
    for (let start = 0; start < handles.length; start += 6) {
      const results = await Promise.allSettled(handles.slice(start, start + 6).map(cardFor));
      if (revision !== wishlistRevision) return;
      results.forEach(result => { if (result.status === 'fulfilled' && result.value) cards.push(result.value); else if (result.status === 'rejected') failed = true; });
    }
    if (revision !== wishlistRevision) return;
    grid.replaceChildren(...cards);
    grid.setAttribute('aria-busy', 'false');
    empty.hidden = cards.length > 0 || failed;
    if (failed) { error.textContent = strings.error; error.hidden = false; }
    syncWishlist();
  }

  async function renderRecent() {
    const section = document.querySelector('[data-recently-viewed]');
    if (!section) return;
    const handles = readHandles(recentKey).filter(handle => handle !== section.dataset.current).slice(0, 4);
    const results = await Promise.allSettled(handles.map(cardFor));
    const cards = results.filter(result => result.status === 'fulfilled' && result.value).map(result => result.value);
    section.querySelector('[data-recent-grid]').replaceChildren(...cards);
    section.querySelector('[data-recent-empty]').hidden = cards.length > 0;
    syncWishlist();
  }

  function rememberProduct(handle) {
    if (!handle || !/^[a-z0-9][a-z0-9-]*$/.test(handle)) return;
    try { localStorage.setItem(recentKey, JSON.stringify([handle, ...readHandles(recentKey).filter(value => value !== handle)].slice(0, 12))); } catch { }
  }

  function initGallery(gallery) {
    if (!gallery || galleryBindings.has(gallery)) return;
    galleryBindings.add(gallery);
    const main = gallery.querySelector('.gallery-main');
    const slides = [...main.querySelectorAll('.gallery-slide')];
    function sync() {
      const index = Math.max(0, Math.min(slides.length - 1, Math.round(main.scrollLeft / (main.clientWidth || 1))));
      gallery.querySelectorAll('[data-gallery-index]').forEach((button, i) => {
        button.classList.toggle('active', i === index);
        button.setAttribute('aria-pressed', String(i === index));
      });
      const counter = gallery.querySelector('[data-gallery-counter]');
      if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
      gallery.querySelectorAll('[data-gallery-step]').forEach(button => { button.disabled = Number(button.dataset.galleryStep) < 0 ? index === 0 : index === slides.length - 1; });
    }
    main.addEventListener('scroll', sync, { passive: true });
    const selected = slides.findIndex(slide => slide.dataset.mediaId === gallery.dataset.featuredMedia);
    if (selected > 0) main.scrollTo({ left: main.clientWidth * selected, behavior: 'instant' });
    sync();
  }

  function initProduct(section) {
    section.querySelectorAll('[data-variant-id]').forEach(input => { input.disabled = false; });
    section.querySelectorAll('[data-option-value-id]').forEach(input => { input.dataset.confirmed = String(input.checked); });
    section.querySelectorAll('[data-gallery]').forEach(initGallery);
  }

  async function selectOption(input) {
    const section = input.closest('[data-product-section]');
    productRequests.get(section)?.abort();
    const controller = new AbortController();
    productRequests.set(section, controller);
    const form = section.querySelector('.reeshan-product-form');
    const oldQuantity = Number(form?.elements.quantity?.value || 1);
    const selected = [...section.querySelectorAll('[data-option-value-id]:checked')].map(option => option.dataset.optionValueId);
    const url = new URL(input.dataset.productUrl || section.dataset.productUrl, location.origin);
    url.searchParams.set('section_id', section.dataset.sectionId);
    url.searchParams.set('option_values', selected.join(','));
    section.setAttribute('aria-busy', 'true');
    section.querySelectorAll('.reeshan-product-form button[type=submit]').forEach(button => { button.disabled = true; });
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error(strings.error);
      const replacement = parse(await response.text()).querySelector('[data-product-section]');
      if (!replacement || !replacement.querySelector('[data-product-info]')) throw new Error(strings.error);
      if (controller.signal.aborted) return;
      const focusId = input.id;
      section.innerHTML = replacement.innerHTML;
      section.dataset.productHandle = replacement.dataset.productHandle;
      section.dataset.productUrl = replacement.dataset.productUrl;
      initProduct(section);
      const quantity = section.querySelector('input[name=quantity]');
      if (quantity) {
        const min = Number(quantity.min || 1);
        const max = quantity.max ? Number(quantity.max) : Infinity;
        const step = Number(quantity.step || 1);
        quantity.value = String(Math.min(max, Math.max(min, min + Math.floor((oldQuantity - min) / step) * step)));
      }
      document.getElementById(focusId)?.focus({ preventScroll: true });
      const address = new URL(section.dataset.productUrl, location.origin);
      const id = section.querySelector('[data-variant-id]')?.value;
      if (id) address.searchParams.set('variant', id); else address.searchParams.set('option_values', selected.join(','));
      const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
      if (previewTheme) address.searchParams.set('preview_theme_id', previewTheme);
      history.replaceState({}, '', address);
      rememberProduct(section.dataset.productHandle);
      syncWishlist();
      window.Shopify?.PaymentButton?.init?.();
    } catch (error) {
      if (error.name === 'AbortError') return;
      section.querySelectorAll('[data-option-value-id]').forEach(option => { option.checked = option.dataset.confirmed === 'true'; });
      const errorNode = section.querySelector('[data-product-error]');
      if (errorNode) { errorNode.textContent = error.message; errorNode.hidden = false; }
      const id = section.querySelector('[data-variant-id]');
      const wasAvailable = !!id?.value && !section.querySelector('.stock-status.unavailable');
      section.querySelectorAll('.reeshan-product-form button[type=submit]').forEach(button => { button.disabled = !wasAvailable; });
    } finally {
      if (productRequests.get(section) === controller) section.removeAttribute('aria-busy');
    }
  }

  function sectionIds() {
    const ids = ['cart-drawer'];
    const main = document.querySelector('[data-main-cart]');
    if (main?.dataset.sectionId) ids.push(main.dataset.sectionId);
    return ids;
  }

  function updateCartSections(sections) {
    let drawerUpdated = false;
    const focused = document.activeElement;
    const focusId = focused?.id;
    const note = document.getElementById('CartNote')?.value;
    const discount = document.getElementById('CartDiscount')?.value;
    Object.values(sections || {}).forEach(html => {
      if (typeof html !== 'string') return;
      const page = parse(html);
      const body = page.querySelector('[data-cart-drawer-body]');
      if (body) {
        document.querySelector('[data-cart-drawer-body]')?.replaceWith(body);
        document.querySelectorAll('[data-cart-count]').forEach(counter => { counter.textContent = body.dataset.itemCount || '0'; });
        drawerUpdated = true;
      }
      const main = page.querySelector('[data-main-cart]');
      if (main) document.querySelector('[data-main-cart]')?.replaceWith(main);
    });
    if (note !== undefined && document.getElementById('CartNote')) document.getElementById('CartNote').value = note;
    if (discount !== undefined && document.getElementById('CartDiscount')) document.getElementById('CartDiscount').value = discount;
    if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
    return drawerUpdated;
  }

  async function refreshCart() {
    const url = new URL(location.pathname, location.origin);
    url.searchParams.set('sections', sectionIds().join(','));
    const response = await jsonRequest(url);
    if (!updateCartSections(response)) throw new Error(strings.cart_error);
  }

  function markCartBusy() {
    document.querySelectorAll('[data-main-cart], [data-cart-drawer-body]').forEach(element => {
      element.setAttribute('aria-busy', String(cartPending > 0));
      element.querySelectorAll('button, input[data-cart-quantity]').forEach(control => { control.disabled = cartPending > 0; });
    });
  }

  function enqueueCart(task) {
    cartPending++;
    markCartBusy();
    const operation = cartQueue.then(task);
    cartQueue = operation.catch(() => {});
    return operation.finally(() => { cartPending--; markCartBusy(); });
  }

  function cartError(error) {
    document.querySelectorAll('[data-cart-error]').forEach(node => { node.textContent = error.message || strings.cart_error; node.hidden = false; });
    notify(error.message || strings.cart_error);
  }

  async function changeLine(key, quantity) {
    if (!Number.isInteger(quantity) || quantity < 0) return;
    return enqueueCart(async () => {
      const response = await jsonRequest(route('cart/change.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: sectionIds(), sections_url: location.pathname })
      });
      if (!updateCartSections(response.sections)) await refreshCart();
      notify(strings.updated);
    }).catch(async error => { try { await refreshCart(); } catch {} cartError(error); });
  }

  async function addProduct(form, buyNow) {
    if (form.dataset.submitting === 'true' || form.closest('[aria-busy=true]')) return;
    if (!form.reportValidity() || !form.querySelector('[data-variant-id]')?.value) return;
    const data = new FormData(form);
    data.set('sections', sectionIds().join(','));
    data.set('sections_url', location.pathname);
    form.dataset.submitting = 'true';
    form.querySelectorAll('button[type=submit]').forEach(button => { button.disabled = true; });
    const errorNode = form.querySelector('[data-product-error]');
    if (errorNode) errorNode.hidden = true;
    try {
      await enqueueCart(async () => {
        const response = await jsonRequest(route('cart/add.js'), { method: 'POST', headers: { Accept: 'application/json' }, body: data });
        if (buyNow) { location.assign(route('checkout')); return; }
        try { if (!updateCartSections(response.sections)) await refreshCart(); }
        catch { location.assign(route('cart')); return; }
        openDialog('CartDrawer', form.querySelector('button[name=add]'));
        notify(strings.added);
      });
    } catch (error) {
      if (errorNode) { errorNode.textContent = error.message; errorNode.hidden = false; }
      notify(error.message);
    } finally {
      delete form.dataset.submitting;
      form.querySelectorAll('button[type=submit]').forEach(button => { button.disabled = false; });
    }
  }

  async function predictiveSearch(input) {
    const output = document.querySelector('[data-predictive-results]');
    if (!output) return;
    if (originalSuggestions === undefined) originalSuggestions = output.innerHTML;
    searchRequest?.abort();
    const query = input.value.trim();
    if (!query) { output.innerHTML = originalSuggestions; output.removeAttribute('aria-busy'); return; }
    const controller = new AbortController();
    searchRequest = controller;
    output.replaceChildren(skeletons(3));
    output.setAttribute('aria-busy', 'true');
    const url = route('search/suggest');
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '3');
    url.searchParams.set('section_id', 'predictive-search');
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(strings.search_error);
      const content = parse(await response.text()).querySelector('[data-predictive-content]');
      if (!content) throw new Error(strings.search_error);
      if (!controller.signal.aborted) output.replaceChildren(content);
    } catch (error) {
      if (error.name !== 'AbortError') output.textContent = strings.search_error;
    } finally { if (searchRequest === controller) output.removeAttribute('aria-busy'); }
  }

  function loadRecommendations(element) {
    if (element.dataset.loaded) return;
    element.dataset.loaded = 'true';
    fetch(element.dataset.url, { credentials: 'same-origin' }).then(response => response.ok ? response.text() : '').then(html => {
      const content = parse(html).querySelector('[data-recommendations]');
      if (content) { element.innerHTML = content.innerHTML; syncWishlist(); }
    }).catch(() => {});
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-product-section]').forEach(initProduct);
    scope.querySelectorAll('[data-gallery]').forEach(initGallery);
    scope.querySelectorAll('[data-recommendations]').forEach(element => {
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); loadRecommendations(element); } }, { rootMargin: '400px' });
        observer.observe(element);
      } else loadRecommendations(element);
    });
    scope.querySelectorAll('[data-form-status]').forEach(status => { const dialog = status.closest('dialog'); if (dialog) openDialog(dialog.id); });
    syncWishlist();
    scope.querySelectorAll('[data-address-country]').forEach(country => {
      if (country.dataset.bound) return;
      country.dataset.bound = 'true';
      const desired = country.dataset.default;
      const option = [...country.options].find(item => item.value === desired || item.textContent === desired);
      if (option) country.value = option.value;
      const populate = () => {
        const province = document.getElementById(country.dataset.provinceId);
        if (!province) return;
        let provinces = [];
        try { provinces = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch {}
        province.replaceChildren(...provinces.map(([value, label]) => new Option(label, value)));
        province.closest('[data-province-container]').hidden = provinces.length === 0;
        const match = [...province.options].find(item => item.value === province.dataset.default || item.textContent === province.dataset.default);
        if (match) province.value = match.value;
      };
      country.addEventListener('change', populate);
      populate();
    });
  }

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const opener = target.closest('[data-dialog-open]');
    if (opener && openDialog(opener.dataset.dialogOpen, opener)) { event.preventDefault(); if (opener.dataset.dialogOpen === 'CartDrawer') cartQueue.then(refreshCart).catch(cartError); return; }
    const closer = target.closest('[data-dialog-close]');
    if (closer) { event.preventDefault(); closeDialog(closer.closest('dialog')); return; }
    if (target.tagName === 'DIALOG') {
      const rect = target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(target);
    }
    const wish = target.closest('[data-wishlist-toggle]');
    if (wish) {
      event.preventDefault();
      const handle = wish.dataset.wishlistToggle;
      const saved = readHandles(wishlistKey);
      const exists = saved.includes(handle);
      if (writeHandles(wishlistKey, exists ? saved.filter(item => item !== handle) : [handle, ...saved].slice(0, 100))) {
        syncWishlist(); renderWishlist(); notify(exists ? strings.removed : strings.saved);
      }
      return;
    }
    const cartStep = target.closest('[data-cart-step], [data-cart-remove]');
    if (cartStep) {
      event.preventDefault();
      if (cartPending) return;
      const item = cartStep.closest('[data-cart-item]');
      const input = item.querySelector('[data-cart-quantity]');
      const amount = cartStep.hasAttribute('data-cart-remove') ? 0 : Math.max(0, Number(input.value) + Number(cartStep.dataset.cartStep) * Number(input.step || 1));
      changeLine(item.dataset.key, amount);
      return;
    }
    const quantityStep = target.closest('[data-quantity-step]');
    if (quantityStep) {
      const input = quantityStep.closest('.quantity').querySelector('input');
      if (Number(quantityStep.dataset.quantityStep) > 0) input.stepUp(); else input.stepDown();
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const galleryButton = target.closest('[data-gallery-index], [data-gallery-step]');
    if (galleryButton) {
      const gallery = galleryButton.closest('[data-gallery]');
      const main = gallery.querySelector('.gallery-main');
      const index = galleryButton.hasAttribute('data-gallery-index') ? Number(galleryButton.dataset.galleryIndex) : Math.round(main.scrollLeft / main.clientWidth) + Number(galleryButton.dataset.galleryStep);
      main.scrollTo({ left: Math.max(0, Math.min(main.children.length - 1, index)) * main.clientWidth, behavior: reducedMotion ? 'instant' : 'smooth' });
      return;
    }
    if (target.closest('[data-share]')) {
      const title = document.querySelector('.product-info h1')?.textContent || document.title;
      if (navigator.share) navigator.share({ title, url: location.href }).catch(error => { if (error.name !== 'AbortError') notify(strings.share_error); });
      else if (navigator.clipboard) navigator.clipboard.writeText(location.href).then(() => notify(strings.copied)).catch(() => notify(strings.share_error));
      else notify(strings.share_error);
    }
  });

  document.addEventListener('cancel', event => { if (event.target.matches('dialog')) { event.preventDefault(); closeDialog(event.target); } }, true);
  document.addEventListener('change', event => {
    const input = event.target;
    if (input.matches('[data-option-value-id]')) selectOption(input);
    else if (input.matches('[data-cart-quantity]')) {
      if (!input.reportValidity()) return;
      changeLine(input.closest('[data-cart-item]').dataset.key, Number(input.value));
    } else if (input.matches('[data-auto-submit]')) input.form?.requestSubmit();
  });
  document.addEventListener('input', event => {
    if (event.target.matches('[data-predictive-input]')) {
      clearTimeout(searchTimer);
      searchRequest?.abort();
      searchTimer = setTimeout(() => predictiveSearch(event.target), 200);
    }
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (form.matches('.reeshan-product-form')) { event.preventDefault(); addProduct(form, event.submitter?.hasAttribute('data-buy-now')); return; }
    if (form.id === 'CartForm' && event.submitter?.name === 'checkout') {
      const discount = form.elements.discount?.value.trim();
      if (!discount) return;
      event.preventDefault();
      enqueueCart(async () => {
        await jsonRequest(route('cart/update.js'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount, note: form.elements.note?.value || '' }) });
        location.assign(route('checkout'));
      }).catch(cartError);
    }
  });
  window.addEventListener('storage', event => { if (event.key === wishlistKey) { syncWishlist(); renderWishlist(); } });
  window.addEventListener('pageshow', event => { if (event.persisted) { syncWishlist(); refreshCart().catch(() => {}); } });
  const compactHeader = () => document.querySelector('[data-sticky-header]')?.classList.toggle('compact', window.scrollY > 36);
  window.addEventListener('scroll', compactHeader, { passive: true });
  document.addEventListener('shopify:section:load', event => { init(event.target); compactHeader(); });
  document.addEventListener('shopify:section:select', event => { event.target.querySelector('dialog')?.showModal?.(); });
  init();
  compactHeader();
  renderWishlist();
  renderRecent();
  rememberProduct(document.querySelector('[data-product-section]')?.dataset.productHandle);
})();
